import React, { useState, useEffect } from 'react';
import useModelService from '../../services/modelService';

const AI_PROVIDERS = [
  { value: 'claude', label: 'Claude (Anthropic)' },
  { value: 'openai', label: 'OpenAI' }
];

const PromptForm = ({ prompt, onSubmit, onCancel }) => {
  const modelService = useModelService();

  // Dynamic model lists
  const [claudeModels, setClaudeModels] = useState([]);
  const [openaiModels, setOpenaiModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: '',
    aiProvider: AI_PROVIDERS[0].value,
    model: '',
    isActive: true,
    reasonForChange: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch available models on component mount
  useEffect(() => {
    const fetchModels = async () => {
      setModelsLoading(true);
      const result = await modelService.getAvailableModels();

      if (result.success) {
        setClaudeModels(result.data.claude);
        setOpenaiModels(result.data.openai);
      } else {
        // Use fallback models from service
        setClaudeModels(result.data.claude);
        setOpenaiModels(result.data.openai);
      }
      setModelsLoading(false);
    };

    fetchModels();
  }, []);

  // Initialize form data when editing an existing prompt
  useEffect(() => {
    if (prompt && claudeModels.length > 0 && openaiModels.length > 0) {
      const aiProvider = prompt.aiProvider || AI_PROVIDERS[0].value;
      const defaultModel = aiProvider === 'openai'
        ? (openaiModels[0]?.value || '')
        : (claudeModels[0]?.value || '');

      setFormData({
        name: prompt.name || '',
        description: prompt.description || '',
        content: prompt.content || '',
        aiProvider: aiProvider,
        model: prompt.aiModel || defaultModel,
        isActive: prompt.isActive !== undefined ? prompt.isActive : true,
        reasonForChange: ''
      });
    }
  }, [prompt, claudeModels, openaiModels]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    } else if (formData.content.length < 10) {
      newErrors.content = 'Content must be at least 10 characters';
    }

    if (!formData.aiProvider) {
      newErrors.aiProvider = 'AI provider selection is required';
    }

    if (!formData.model) {
      newErrors.model = 'Model selection is required';
    }

    if (prompt && !formData.reasonForChange.trim()) {
      newErrors.reasonForChange = 'Reason for change is required when updating a prompt';
    } else if (prompt && formData.reasonForChange.length < 5) {
      newErrors.reasonForChange = 'Reason for change must be at least 5 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // If changing AI provider, reset model to first option of new provider
      if (field === 'aiProvider') {
        const newModels = value === 'openai' ? openaiModels : claudeModels;
        newData.model = newModels[0]?.value || '';
      }

      return newData;
    });

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('content');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentContent = formData.content;
    
    const newContent = 
      currentContent.substring(0, start) + 
      `{${placeholder}}` + 
      currentContent.substring(end);
    
    handleChange('content', newContent);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length + 2, start + placeholder.length + 2);
    }, 10);
  };

  const commonPlaceholders = [
    'sourceLang',
    'targetLang',
    'additionalPrompt'
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="flex justify-between items-center border-b border-gray-200 pb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {prompt ? 'Edit Prompt' : 'Create New Prompt'}
            </h3>
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Prompt Name *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="e.g., LEGAL_TRANSLATION_PROMPT"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Brief description of the prompt's purpose"
              />
            </div>

            {prompt && (
              <div className="md:col-span-2">
                <label htmlFor="reasonForChange" className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Change *
                </label>
                <textarea
                  id="reasonForChange"
                  value={formData.reasonForChange}
                  onChange={(e) => handleChange('reasonForChange', e.target.value)}
                  rows={3}
                  className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                    errors.reasonForChange ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Explain why you are making changes to this prompt..."
                />
                {errors.reasonForChange && (
                  <p className="mt-1 text-sm text-red-600">{errors.reasonForChange}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="aiProvider" className="block text-sm font-medium text-gray-700 mb-2">
                AI Provider *
              </label>
              <select
                id="aiProvider"
                value={formData.aiProvider}
                onChange={(e) => handleChange('aiProvider', e.target.value)}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.aiProvider ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                {AI_PROVIDERS.map((provider) => (
                  <option key={provider.value} value={provider.value}>
                    {provider.label}
                  </option>
                ))}
              </select>
              {errors.aiProvider && (
                <p className="mt-1 text-sm text-red-600">{errors.aiProvider}</p>
              )}
            </div>

            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                AI Model *
              </label>
              <select
                id="model"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                disabled={modelsLoading}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  errors.model ? 'border-red-300' : 'border-gray-300'
                } ${modelsLoading ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              >
                {modelsLoading ? (
                  <option>Loading models...</option>
                ) : (
                  (formData.aiProvider === 'openai' ? openaiModels : claudeModels).map((model) => (
                    <option key={model.value} value={model.value}>
                      {model.label}
                    </option>
                  ))
                )}
              </select>
              {errors.model && (
                <p className="mt-1 text-sm text-red-600">{errors.model}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center">
                <input
                  id="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Active (prompt is enabled for use)
                </label>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="content" className="block text-sm font-medium text-gray-700">
                  Prompt Content *
                </label>
                <div className="flex gap-1 flex-wrap">
                  {commonPlaceholders.map((placeholder) => (
                    <button
                      key={placeholder}
                      type="button"
                      onClick={() => insertPlaceholder(placeholder)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 whitespace-nowrap"
                    >
                      +{placeholder}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                rows={12}
                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                  errors.content ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter your prompt content here. Use {sourceLang}, {targetLang}, {additionalPrompt} for dynamic values..."
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content}</p>
              )}
              <div className="mt-1 text-xs text-gray-500">
                Use placeholders like {'{sourceLang}'}, {'{targetLang}'}, {'{additionalPrompt}'} for dynamic content
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {loading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {loading ? 'Saving...' : (prompt ? 'Update Prompt' : 'Create Prompt')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromptForm;